import React, { useEffect, useRef, useState } from "react";
import styled from '@emotion/styled';
import { Colors } from "../palettes/colorPalette";
import { backgroundTexture } from "../uiConstants";
import { UIButtonSmall } from "../styles/ButtonStyle";
import { captureBugReportScreenshot, submitBugReport } from "../bugReport/bugReporter";
import { formatReportId } from "../bugReport/reportId";
import { GameClient } from "../gameClient";
import { getSystem } from "../../../shared/utils";
import { PlacementSystem } from "../systems/placementSystem";

declare var gameClient: GameClient;

const CrashButton = styled(UIButtonSmall)`
	min-width: 8vw;
	max-width: 8vw;
	font-size: 0.8vw;
	border-width: .4vw;
	border-radius: 1.6vh;

	&:hover {
		border-width: .4vw;
	}
`;

type ReportStatus =
	| { state: 'unsent' }
	| { state: 'sending' }
	| { state: 'sent', reportId: string }
	| { state: 'failed', message: string };

interface CrashModalProps {
	/** Our description of the crash, sent along with whatever the player writes */
	reportText: string;
	/** Shows a "Reload Last Save" button when given */
	onReloadSave?: () => void;
	onBackToMenu: () => void;
}

/**
 * Tells the player the game crashed, and lets them send us a report about it.
 * The screenshot is taken before anything is shown, so it's of the game rather than this popup.
 * System info is left out, it's only needed for graphics issues.
 */
export const CrashModal = ({ reportText, onReloadSave, onBackToMenu }: CrashModalProps) => {
	const [screenshot, setScreenshot] = useState<Uint8Array | null | undefined>(undefined); //undefined while capturing
	const [description, setDescription] = useState('');
	const [status, setStatus] = useState<ReportStatus>({ state: 'unsent' });
	const descriptionRef = useRef<HTMLTextAreaElement | null>(null);

	useEffect(() => {
		let mounted = true;
		captureBugReportScreenshot().then(image => {
			if (mounted) setScreenshot(image);
		});
		return () => { mounted = false; };
	}, []);

	//The game listens for keys on the document (camera, hotkeys), which React 16 can't stop
	//from its own handlers since it listens there too. So stop typing here from reaching it natively.
	useEffect(() => {
		const element = descriptionRef.current;
		if (!element) return;
		const stop = (event: Event) => event.stopPropagation();
		const types = ['keydown', 'keyup', 'keypress'];
		types.forEach(type => element.addEventListener(type, stop));
		return () => types.forEach(type => element.removeEventListener(type, stop));
	}, [screenshot !== undefined]);

	if (screenshot === undefined) return null;

	const sendReport = async () => {
		if (status.state === 'sending' || status.state === 'sent') return;
		setStatus({ state: 'sending' });
		const playerText = description.trim();
		const result = await submitBugReport({
			description: playerText ? `${playerText}\n\n---\n${reportText}` : reportText,
			includeSystemInfo: false,
			screenshot,
		});
		setStatus(result.ok ?
			{ state: 'sent', reportId: result.reportId } :
			{ state: 'failed', message: result.message });
	};

	//Keep clicks on the popup away from the game underneath
	const blockGameInput = () => {
		if (gameClient.world !== undefined) {
			getSystem(gameClient.world, PlacementSystem)?.signalUIInterceptThisFrame();
		}
	};

	const sent = status.state === 'sent';

	return (
		<div
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				width: '100vw',
				height: '100vh',
				background: 'rgba(0,0,0,0.5)',
				zIndex: 20, //above the loading curtain
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
			onMouseDown={blockGameInput}
			onMouseUp={blockGameInput}
			onMouseMove={blockGameInput}
		>
			<div style={{
				background: `url(${backgroundTexture})`,
				backgroundColor: Colors.UIBackground,
				border: '5px solid black',
				borderRadius: '15px',
				padding: '24px 28px',
				width: '450px',
				maxWidth: '90vw',
				color: Colors.UITextAndBorders,
				fontSize: '18px',
				textAlign: 'center',
			}}>
				<div style={{ fontSize: '1.3em', marginBottom: '16px', color: '#c62828' }}>Something went wrong and the game crashed</div>
				{!sent &&
					<textarea
						ref={descriptionRef}
						value={description}
						onChange={event => setDescription(event.target.value)}
						disabled={status.state === 'sending'}
						placeholder="What were you doing when it happened? (optional)"
						rows={4}
						style={{
							display: 'block',
							width: '90%', //a little narrower than the title
							margin: '0 auto',
							boxSizing: 'border-box',
							fontSize: '0.85em',
							fontFamily: 'inherit',
							padding: '8px',
							resize: 'none',
						}}
					/>
				}
				{!sent &&
					<div style={{ margin: '8px 0 16px' }}>
						<CrashButton onClick={sendReport} disabled={status.state === 'sending'}>Send Report</CrashButton>
					</div>
				}
				{!sent &&
					<div style={{ fontSize: '0.85em', marginBottom: '16px' }}>
						Sorry. Please send the report using the above button. We will use the information you share to make Auto Immune better.
					</div>
				}
				<div style={{ fontSize: '0.85em', marginBottom: '16px', userSelect: 'text' }}>
					{status.state === 'sending' && 'Sending report…'}
					{status.state === 'sent' && `Thanks! Issue reported (${formatReportId(status.reportId)})`}
					{status.state === 'failed' && `Couldn't send the report: ${status.message}`}
				</div>
				<div style={{ display: 'flex', justifyContent: 'center', gap: '24px' }}>
					{onReloadSave && <CrashButton onClick={onReloadSave}>Reload Last Save</CrashButton>}
					<CrashButton onClick={onBackToMenu}>Back to Menu</CrashButton>
				</div>
			</div>
		</div>
	);
};
